<template>
  <div ref="rootEl" class="image-annotation-canvas">
    <canvas ref="canvasEl"></canvas>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, inject, computed, watch } from "vue";
import { Canvas, FabricImage, FabricObject, PencilBrush, Point, Path, Textbox } from "fabric";
import { useStore } from "vuex";
import { IMAGE_ANNOTATION_TOOL, NAMESPACE } from "@/modules/annotation/service/annotation.constant";
import type EventBus from "@plugins/common/service/eventbus";
import { CANVAS_TEXT_SETTINGS } from "@plugins/drawing/drawing.config";
import { createStamp, preloadStampIcons } from "@modules/annotation/service/image.annotation.stamp";
import { STAMP_ICON_URLS_TEACHER } from "@modules/annotation/service/image.annotation.stamp";
import {
  elementsToFabric,
  fabricToElements,
} from "@/modules/annotation/service/image.annotation.seralization";
import {
  buildImageAnnotationRequest,
  getImageIndexPosition,
} from "@modules/annotation/service/image.annotation.request";
import type { AnnotationContext, ImageAnnotationDataModel } from "@/modules/annotation/types";
import { useAnnotation } from "@modules/annotation/composables/annotation.ts";
import { useAnnotationAttemptAdapter } from "@modules/annotation/composables/use-annotation-attempt-adapter.ts";
import { useImageAnnotationDefaults } from "@modules/annotation/composables/use-image-annotation-defaults.ts";
import { getImageCanvasDimensions } from "@modules/annotation/service/image.annotation.canvas.ts";

interface Props {
  /** Bus shared only with the toolbar of the same image. */
  eventBus: EventBus;
  imageUrl: string | undefined;
  /** Full media anchor, i.e. `<parentAnchor>_<index>`. */
  anchor?: string;
  /** Index token of the image inside its rich text. */
  index?: string;
  mediaTitle?: string;
  mediaUuid?: string;
  /** Optional overrides, derived from the annotation context when omitted. */
  annotationResourceType?: string;
  annotationResourceId?: string | number;
  ownerType?: string;
  maxWidth?: number;
  textBoxSettings?: Record<string, unknown>;
  stampIconsSelection?: Record<string, string>;
}

interface BaseFlag {
  isBaseImage?: boolean;
}

interface ExistingAnnotationFlag {
  isExistingAnnotation?: boolean;
}

interface SavedImageAnnotation {
  annotationResourceId: string | number;
  data?: ImageAnnotationDataModel;
  outdated: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  anchor: "",
  index: "",
  mediaTitle: "",
  mediaUuid: "",
  annotationResourceType: undefined,
  annotationResourceId: undefined,
  ownerType: undefined,
  maxWidth: 560,
  textBoxSettings: () => CANVAS_TEXT_SETTINGS as Record<string, unknown>,
  stampIconsSelection: () => ({ ...STAMP_ICON_URLS_TEACHER }),
});

const emit = defineEmits<{
  done: [];
}>();

const { createAnnotation, setWithNote } = useAnnotation();
const { defaultColor, defaultThickness } = useImageAnnotationDefaults();
const attemptAdapter = useAnnotationAttemptAdapter();
const store = useStore();
const annotationContext = inject<AnnotationContext | undefined>("annotationContext", undefined);

const STAMP_META_KEYS = ["elementType", "stampShape", "isBaseImage", "isExistingAnnotation"];
const EXISTING_ANNOTATION_OPACITY = 0.4;
interface StampObjectData {
  elementType?: "stamp";
  stampShape?: string;
}

export type StampObject = FabricObject & StampObjectData;

// Non-reactive fabric handles.
let canvas: Canvas | null = null;
let brush: PencilBrush | null = null; // need to set this to initial value
let previewStamp: StampObject | null = null;
let previewPosition: Point | null = null;
let canvasW = 0;
let canvasH = 0;

// Undo history of dataless snapshots (mirrors reference saveCanvasState).
let initialState: Record<string, unknown> | null = null;
const history: Record<string, unknown>[] = [];

// reactive handles.
const rootEl = ref<HTMLElement | null>(null);
const canvasEl = ref<HTMLCanvasElement | null>(null);
const penWidth = ref(defaultThickness);
const penColor = ref(defaultColor.value);
const textColor = ref(defaultColor.value);
const hasElements = ref(false);
const canUndo = ref(false);
const activeTool = ref<string>("");
const stampShape = ref<string | "">("");

const sortedImageAnnotations = computed<SavedImageAnnotation[]>(() =>
  store.getters[`${NAMESPACE.ANNOTATION}/getSortedImageAnnotations`].filter(
    (ann: SavedImageAnnotation) =>
      !ann.outdated && String(ann.annotationResourceId) === String(props.annotationResourceId),
  ),
);

function isBase(obj: FabricObject): boolean {
  return (obj as FabricObject & BaseFlag).isBaseImage === true;
}

function isExistingAnnotation(obj: FabricObject): boolean {
  return (obj as FabricObject & ExistingAnnotationFlag).isExistingAnnotation === true;
}

function annotationObjects(): FabricObject[] {
  return canvas ? canvas.getObjects().filter((o) => !isBase(o) && !isExistingAnnotation(o)) : [];
}

// Saved annotations sit right above the base image so new drawings stay on top.
function renderExistingAnnotations() {
  if (!canvas) return;
  const c = canvas;
  c.getObjects()
    .filter(isExistingAnnotation)
    .forEach((o) => c.remove(o));

  const objs = sortedImageAnnotations.value.flatMap((ann) =>
    elementsToFabric(ann.data?.elements ?? [], canvasW, canvasH, props.stampIconsSelection),
  );
  objs.forEach((obj) => {
    obj.set({
      opacity: EXISTING_ANNOTATION_OPACITY,
      selectable: false,
      evented: false,
      hoverCursor: "default",
    });
    (obj as FabricObject & ExistingAnnotationFlag).isExistingAnnotation = true;
    obj.setCoords();
  });
  if (objs.length) c.insertAt(1, ...objs);
  c.requestRenderAll();
}

watch(sortedImageAnnotations, () => renderExistingAnnotations());

function refreshFlags() {
  hasElements.value = annotationObjects().length > 0;
  canUndo.value = history.length > 0;
  props.eventBus.$emit("canvasModified", {
    modified: canUndo.value,
    annotationObjects: annotationObjects(),
  });
}

function snapshot(): Record<string, unknown> {
  return canvas!.toDatalessJSON(STAMP_META_KEYS) as unknown as Record<string, unknown>;
}

function saveCanvasState() {
  if (!canvas) return;
  history.push(snapshot());
  refreshFlags();
}

onMounted(async () => {
  await initCanvas();
});

onBeforeUnmount(() => {
  canvas?.dispose();
  canvas = null;
});

async function initCanvas() {
  await preloadStampIcons(props.stampIconsSelection);
  const img = await FabricImage.fromURL(props.imageUrl as string, {
    crossOrigin: "anonymous",
  });
  const natW = img.width ?? props.maxWidth;
  const natH = img.height ?? props.maxWidth;
  const availableWidth = rootEl.value?.clientWidth || props.maxWidth;
  const maxWidth = Math.min(props.maxWidth, availableWidth);
  const dimensions = getImageCanvasDimensions(natW, natH, maxWidth, window.innerHeight * 0.6);
  canvasW = dimensions.width;
  canvasH = dimensions.height;

  canvas = new Canvas(canvasEl.value as HTMLCanvasElement, {
    width: canvasW,
    height: canvasH,
    backgroundColor: "#ffffff",
    isDrawingMode: false,
    selection: false,
    preserveObjectStacking: true,
    enableRetinaScaling: false,
  });

  FabricObject.prototype.set({ transparentCorners: false });

  brush = new PencilBrush(canvas);
  brush.width = penWidth.value;
  brush.color = hexToRGB(penColor.value, 1);
  canvas.freeDrawingBrush = brush;

  // Base image (non-interactive), scaled to fill the canvas.
  img.set({
    left: 0,
    top: 0,
    originX: "left",
    originY: "top",
    scaleX: canvasW / natW,
    scaleY: canvasH / natH,
    selectable: false,
    evented: false,
    erasable: false,
    hoverCursor: "default",
  });
  (img as FabricImage & BaseFlag).isBaseImage = true;
  canvas.add(img);
  canvas.sendObjectToBack(img);
  renderExistingAnnotations();
  canvas.renderAll();

  bindEvents();
  initialState = snapshot();
  refreshFlags();

  // Let the toolbar anchor its config popup to the canvas (exactly the image bounds).
  props.eventBus.$emit("canvasReady", { element: canvas.wrapperEl });
}

function bindEvents() {
  if (!canvas) return;
  const c = canvas;

  c.on("path:created", (ev) => {
    const path = (ev as unknown as { path: Path }).path;
    path.set({ selectable: true, hasControls: true, lockRotation: true, erasable: false });
    path.setControlsVisibility({ mtr: false });
    saveCanvasState();
  });

  c.on("mouse:down", (opt) => {
    if (!canvas) return;

    const scene = canvas.getScenePoint(opt.e);

    if (inBounds(scene)) {
      switch (activeTool.value) {
        case IMAGE_ANNOTATION_TOOL.DRAW:
          // Let the toolbar close the draw popup once the user starts drawing.
          props.eventBus.$emit("drawStart");
          break;
        case IMAGE_ANNOTATION_TOOL.STAMP:
          if (stampShape.value) placeStamp(scene, stampShape.value);
          props.eventBus.$emit("stampPlaced");
          break;
        case IMAGE_ANNOTATION_TOOL.TEXT:
          props.eventBus.$emit("textStart");
          addTextBox(scene);
          break;
      }
    }
  });

  c.on("mouse:move", (opt) => {
    if (activeTool.value === IMAGE_ANNOTATION_TOOL.STAMP && stampShape.value) {
      updatePreview(canvas!.getScenePoint(opt.e));
    }
  });

  c.on("object:moving", (e) => clampObject(e.target as FabricObject));
  c.on("object:scaling", (e) => clampObject(e.target as FabricObject));
  c.on("object:modified", () => saveCanvasState());
  c.on("selection:cleared", () => refreshFlags());
}

function inBounds(p: Point): boolean {
  return p.x >= 0 && p.y >= 0 && p.x <= canvasW && p.y <= canvasH;
}

function clampObject(obj: FabricObject) {
  const rect = obj.getBoundingRect();
  if (obj.left! - (obj.originX === "center" ? rect.width / 2 : 0) < 0) {
    obj.left = obj.originX === "center" ? rect.width / 2 : 0;
  }
  if (obj.top! - (obj.originY === "center" ? rect.height / 2 : 0) < 0) {
    obj.top = obj.originY === "center" ? rect.height / 2 : 0;
  }
  const right = obj.left! + (obj.originX === "center" ? rect.width / 2 : rect.width);
  const bottom = obj.top! + (obj.originY === "center" ? rect.height / 2 : rect.height);
  if (right > canvasW)
    obj.left = canvasW - (obj.originX === "center" ? rect.width / 2 : rect.width);
  if (bottom > canvasH)
    obj.top = canvasH - (obj.originY === "center" ? rect.height / 2 : rect.height);
  obj.setCoords();
}

// Toolbar handling
function handleTool(tool: string) {
  if (!canvas) return;
  canvas.discardActiveObject();
  removePreview();
  activeTool.value = tool;
  stampShape.value = "";

  const drawing = tool === IMAGE_ANNOTATION_TOOL.DRAW;
  canvas.isDrawingMode = drawing;
  canvas.selection = tool === IMAGE_ANNOTATION_TOOL.SELECT;
  canvas.defaultCursor =
    tool === IMAGE_ANNOTATION_TOOL.TEXT || tool === IMAGE_ANNOTATION_TOOL.DRAW
      ? "crosshair"
      : "default";

  if (drawing && brush) {
    brush.width = penWidth.value;
    brush.color = hexToRGB(penColor.value, 1);
  }

  const selectable = tool === IMAGE_ANNOTATION_TOOL.SELECT;
  annotationObjects().forEach((o) => {
    o.selectable = selectable;
    o.evented = selectable;
    o.lockRotation = true;
    o.setControlsVisibility({ mtr: false });
  });
  canvas.renderAll();
}

// --- Draw -------------------------------------------------------------------

function setPenColor(value: string) {
  penColor.value = value;
  if (brush) brush.color = hexToRGB(value, 1);
}

function setPenWidth(size: number) {
  penWidth.value = size;
  if (brush) brush.width = size;
}

// --- Stamp ------------------------------------------------------------------

function setStamp(shape: string) {
  const previousPosition = previewPosition;
  const shapeChanged = stampShape.value !== shape;
  stampShape.value = shape;
  activeTool.value = IMAGE_ANNOTATION_TOOL.STAMP;
  if (canvas) {
    canvas.isDrawingMode = false;
    canvas.selection = false;
    canvas.defaultCursor = "crosshair";
    if (shapeChanged && previousPosition) {
      removePreview();
      updatePreview(previousPosition);
    }
  }
}

function updatePreview(scene: Point) {
  if (!canvas || !stampShape.value) return;
  if (!inBounds(scene)) {
    removePreview();
    canvas.defaultCursor = "default";
    return;
  }
  const shape = stampShape.value;
  if (previewStamp?.stampShape !== shape) {
    removePreview();
  }
  canvas.defaultCursor = "crosshair";
  previewPosition = scene;
  if (!previewStamp) {
    previewStamp = createStamp(shape, props.stampIconsSelection);
    previewStamp.set({ opacity: 0.5, evented: false, selectable: false });
    canvas.add(previewStamp);
  }
  previewStamp.set({ left: scene.x, top: scene.y });
  previewStamp.setCoords();
  canvas.requestRenderAll();
}

function removePreview() {
  if (canvas && previewStamp) {
    canvas.remove(previewStamp);
    previewStamp = null;
    canvas.defaultCursor = "default";
    canvas.requestRenderAll();
  }
}

function placeStamp(scene: Point, shape: string) {
  if (!canvas) return;
  removePreview();
  const stamp = createStamp(shape, props.stampIconsSelection);
  stamp.set({ left: scene.x, top: scene.y, selectable: false, evented: false });
  stamp.setCoords();
  canvas.add(stamp);
  canvas.renderAll();
  saveCanvasState();
  // Stamp placed -> return to Select (AC 107).
  stampShape.value = "";
  handleTool(IMAGE_ANNOTATION_TOOL.SELECT);
}

// --- Text -------------------------------------------------------------------

function setTextColor(value: string) {
  textColor.value = value;
}

function addTextBox(scene: Point) {
  if (!canvas) return;

  const textbox = new Textbox(
    (props.textBoxSettings as { placeholderText?: string }).placeholderText as string,
    {
      left: scene.x,
      top: scene.y,
      originX: "center",
      originY: "center",
      width: CANVAS_TEXT_SETTINGS.textBox.width,
      fontSize: CANVAS_TEXT_SETTINGS.textBox.fontSize,
      fontFamily: CANVAS_TEXT_SETTINGS.textBox.fontFamily,
      fill: textColor.value,
      lockRotation: true,
      backgroundColor: "transparent",
      erasable: true,
    },
  );
  textbox.setControlsVisibility({ mtr: false });
  canvas.add(textbox);
  // Enter Select mode without discarding the freshly created textbox so the
  // user can type immediately (typing replaces the selected placeholder).
  activeTool.value = IMAGE_ANNOTATION_TOOL.SELECT;
  canvas.isDrawingMode = false;
  canvas.selection = true;
  annotationObjects().forEach((o) => {
    o.selectable = true;
    o.evented = true;
  });
  canvas.setActiveObject(textbox);
  textbox.enterEditing();
  textbox.selectAll();
  saveCanvasState();
}

// --- Undo -------------------------------------------------------------------

function undo() {
  activeTool.value = "";
  if (!canvas || history.length === 0) return;
  history.pop();
  const target = history.length > 0 ? history[history.length - 1] : initialState;
  if (!target) return;
  canvas.loadFromJSON(target).then(() => {
    if (!canvas) return;
    const base = canvas.getObjects().find((o) => isBase(o));
    if (base) {
      base.set({ selectable: false, evented: false, hoverCursor: "default" });
      (base as FabricObject & BaseFlag).isBaseImage = true;
      canvas.sendObjectToBack(base);
    }
    // Snapshots don't keep read-only state, and the saved annotations may have changed since.
    renderExistingAnnotations();
    const selectable = activeTool.value === IMAGE_ANNOTATION_TOOL.SELECT;
    annotationObjects().forEach((o) => {
      o.selectable = selectable;
      o.evented = selectable;
    });
    canvas.renderAll();
    refreshFlags();
  });
}

// --- Done / Back ------------------------------------------------------------

function buildSaveResult(): ImageAnnotationDataModel {
  const objects = annotationObjects();
  const elements = fabricToElements(objects, canvasW, canvasH, defaultColor.value);
  // const boundingBox = fabricBoundingBox(objects, canvasW, canvasH);
  return { type: "image", schemaVersion: 1, elements };
}

//////////////////////////////////////////////////////////

function hexToRGB(hex: string, alpha = 1): string {
  const normalized = hex.replace("#", "");
  const bigint = parseInt(
    normalized.length === 3
      ? normalized
          .split("")
          .map((c) => c + c)
          .join("")
      : normalized,
    16,
  );
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

props.eventBus.$on("toolSelected", (tool: string) => {
  handleTool(tool);
});

props.eventBus.$on("textColorChange", (color: string) => {
  setTextColor(color);
});

props.eventBus.$on("penColorChange", (color: string) => {
  setPenColor(color);
});

props.eventBus.$on("penWidthChange", (width: number) => {
  setPenWidth(width);
});

props.eventBus.$on("stampChange", (stampShape: string) => {
  setStamp(stampShape);
});

props.eventBus.$on("undo", () => {
  undo();
});

props.eventBus.$on("done", () => {
  const saveResult = buildSaveResult();
  // Nothing to save (Done is disabled in this case, this only guards against a race)
  if (saveResult.elements.length === 0) return;

  const requestData = buildImageAnnotationRequest({
    saveResult,
    annotationContext,
    anchor: props.anchor,
    annotationResourceId: props.annotationResourceId,
    annotationResourceType: props.annotationResourceType,
    ownerType: props.ownerType,
    fallbackOwnerId: attemptAdapter.currentUserId.value,
    title: props.mediaTitle,
    mediaUuid: props.mediaUuid,
    indexPosition: getImageIndexPosition(rootEl.value, props.index),
    creatorName: store.getters[`${NAMESPACE.AUTH}/userDisplayName`],
    createdBy: parseInt(store.getters[`${NAMESPACE.AUTH}/userId`]),
  });

  setWithNote(true);
  createAnnotation(requestData);

  emit("done");
});
</script>

<style lang="scss">
@use "@styles/themes/sls-theme/tokens/colors" as *;
.image-annotation-canvas {
  display: flex;
  justify-content: center;
  background-color: $ui-03;
}
</style>
